import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ScoreMarketFormModal from 'r/ui/ScoreMarketFormModal';
import { fetchApiRaw, GET_ANALYSES_CMS } from 'r/utils/fetchApiRaw';
import type { Analyse } from 'r/types';
import { useScoreMarketByAnalysis } from '../ScoreMarketsTab/queries';

export default function AnalysisScoreMarketRoute() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- Analysis ---
  const cachedAnalysis = queryClient
    .getQueriesData<{ data: Analyse[] }>({ queryKey: ['cms-analyses'] })
    .flatMap(([, page]) => page?.data ?? [])
    .find((a) => a.id === analysisId);

  const { data: fetchedAnalysis } = useQuery({
    queryKey: ['cms-analysis', analysisId],
    queryFn: async () => {
      const res = await fetchApiRaw(GET_ANALYSES_CMS, {
        queryParams: {
          filter: encodeURIComponent(`"id":["${analysisId}"]`),
          take: '1',
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return (data[0] as Analyse) ?? null;
    },
    enabled: !cachedAnalysis && !!analysisId,
    staleTime: 5 * 60 * 1000,
  });

  const analysis = cachedAnalysis ?? fetchedAnalysis ?? undefined;

  // --- Existing market ---
  const { data: market, isPending: isMarketPending } =
    useScoreMarketByAnalysis(analysisId);

  const handleClose = () => {
    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate('/?tab=analyses');
    }
  };

  if (!analysis || isMarketPending) return null;

  return (
    <ScoreMarketFormModal
      mode={market ? 'edit' : 'create'}
      analysis={analysis}
      market={market}
      onClose={handleClose}
    />
  );
}
